import { useEffect, useMemo, useState, useCallback } from 'preact/hooks'
import {
    Globe, ExternalLink, Copy, Check, ArrowRight, Loader2,
    RotateCcw, AlertCircle, Rocket, AlertTriangle,
    EyeOff, Pencil, FolderOpen, Filter,
} from 'lucide-preact'
import type { WebViewDto } from '@/api/webs'
import type { LandingPageDeploymentDto } from '@/types/landingPage'
import { BRAND_CLASSES } from '@/constants/theme'
import { DEPLOYMENT_STATUS_STYLES, getDeploymentStatusLabel } from '@/components/webs/constants/deploymentMeta'
import { useDeploymentSearchFilter } from '@/hooks/useDeploymentSearchFilter'
import { useLandingPage } from '@/hooks/useLandingPage'
import { landingPagesApi } from '@/api/landingPages'
import { assetApi } from '@/api/asset'
import { shortAgo } from '@/utils/time'
import { useToast } from '@/hooks/useToast'
import { useAssetReviewPermissions } from '@/hooks/useAssetReviewPermissions'
import { useUserProfile } from '@/hooks/useUserProfile'
import { DataGrid, type ColumnConfig } from '@/components/common/grids/DataGrid'
import type { WebTabKey } from '@/hooks/useWebTabRouter'
import { ExternalLandingPageCard } from '@/components/webs/landingPage/ExternalLandingPageCard'
import { ExternalLandingPageTracking } from '@/components/webs/landingPage/ExternalLandingPageTracking'
import { LandingPageAnalyticsCard } from '@/components/webs/landingPage/LandingPageAnalyticsCard'
import { SaveAsTemplateControl } from '@/components/webs/landingPage/SaveAsTemplateControl'
import { ExternalUrlModal } from '@/components/webs/modals/ExternalUrlModal'

interface Props {
    web: WebViewDto
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    routeToTab: (tab: WebTabKey) => void
}

const DEPLOYMENT_COLUMNS: ColumnConfig<LandingPageDeploymentDto>[] = [
    {
        key: 'version',
        header: 'Deploy #',
        sortable: true,
        accessor: d => d.VersionNumber,
        render: d => <span className="font-medium text-slate-900 dark:text-white">#{d.VersionNumber}</span>,
    },
    {
        key: 'sourceVersion',
        header: 'Content Version',
        sortable: true,
        accessor: d => d.SourceVersionNumber,
        render: d => <span>v{d.SourceVersionNumber}</span>,
    },
    {
        key: 'status',
        header: 'Status',
        sortable: true,
        accessor: d => d.Status,
        render: d => {
            const style = DEPLOYMENT_STATUS_STYLES[d.Status]
            const Icon = style.icon
            return (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${style.cls}`}>
                    <Icon className={`w-3 h-3 ${d.Status === 'Pending' ? 'animate-spin' : ''}`} />
                    {style.label}
                </span>
            )
        },
    },
    {
        key: 'deployedAt',
        header: 'Date',
        sortable: true,
        // Date and Time render two halves of the same instant, so both sort by the whole instant —
        // sorting Time by what it displays would interleave yesterday's 09:00 with today's. Dates,
        // not the raw ISO strings, so the grid compares timestamps rather than collating text.
        accessor: d => new Date(d.DeployedAt),
        render: d => (
            <span>{new Date(d.DeployedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        ),
    },
    {
        key: 'deployedTime',
        header: 'Time',
        sortable: true,
        accessor: d => new Date(d.DeployedAt),
        render: d => (
            <span className="text-slate-500 dark:text-slate-400">
                {new Date(d.DeployedAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
            </span>
        ),
    },
    {
        key: 'notes',
        header: 'Notes',
        render: d => {
            if (d.Status === 'Failed' && d.Error) {
                return <span className="text-xs text-red-600 dark:text-red-400">{d.Error}</span>
            }
            if (d.Status === 'RolledBack') {
                return <span className="text-xs text-slate-500 dark:text-slate-400">Rolled back</span>
            }
            return null
        },
    },
]

export function LandingPageTab({ web, clientCompanyId, workspaceId, campaignId, routeToTab }: Props) {
    const { showError, showSuccess } = useToast()
    const { canEditInWebster } = useAssetReviewPermissions(clientCompanyId, workspaceId)
    const { getWorkspaceCapabilities } = useUserProfile()
    const { canEdit } = getWorkspaceCapabilities(clientCompanyId, workspaceId)
    const { landingPage, setLandingPage, loading, error, load, setMode } = useLandingPage({
        clientCompanyId, workspaceId, campaignId, webId: web.Id,
    })

    const isExternalMode = landingPage?.Mode === 'External'

    const [deployments, setDeployments] = useState<LandingPageDeploymentDto[]>([])
    const [deploymentsLoading, setDeploymentsLoading] = useState(false)
    const [latestVersionNumber, setLatestVersionNumber] = useState<number>(0)
    const [assetStatus, setAssetStatus] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)
    const [deploying, setDeploying] = useState(false)
    const [confirmUnpublish, setConfirmUnpublish] = useState(false)
    const [bypassOpen, setBypassOpen] = useState(false)
    const [rollbackBusyVersion, setRollbackBusyVersion] = useState<number | null>(null)
    const [confirmRollbackVersion, setConfirmRollbackVersion] = useState<number | null>(null)

    useEffect(() => { load() }, [load])

    const loadDeployments = useCallback(() => {
        setDeploymentsLoading(true)
        landingPagesApi.listDeployments(workspaceId, web.Id)
            .then(res => { if (res.data) setDeployments(res.data) })
            .finally(() => setDeploymentsLoading(false))
    }, [workspaceId, web.Id])

    useEffect(() => {
        if (!landingPage || isExternalMode) return
        loadDeployments()
    }, [landingPage, isExternalMode, loadDeployments])

    // The LP entity no longer carries CurrentVersionNumber. The latest content
    // version lives on the linked WebAsset, so fetch it for the deploy banner
    // and to gate the Deploy button on sign-off status.
    useEffect(() => {
        const assetId = landingPage?.AssetId
        if (!assetId || isExternalMode) {
            setLatestVersionNumber(0)
            setAssetStatus(null)
            return
        }
        let cancelled = false
        assetApi.getAssetDetail(clientCompanyId, workspaceId, campaignId, assetId)
            .then(res => {
                if (cancelled || !res.data) return
                setLatestVersionNumber(res.data.CurrentVersionNumber)
                setAssetStatus(res.data.Status)
            })
        return () => { cancelled = true }
    }, [landingPage?.AssetId, clientCompanyId, workspaceId, campaignId, isExternalMode])

    // Memoised because the identity of this array, not just its contents, now matters: it feeds
    // useDeploymentSearchFilter, whose memos key on it. Rebuilding it every render defeated them,
    // re-running the filter and the status-options scan on every keystroke elsewhere in the tab.
    const sortedDeployments = useMemo(
        () => [...deployments].sort((a, b) => b.VersionNumber - a.VersionNumber),
        [deployments],
    )

    const {
        searchQuery, setSearchQuery, statusFilter, setStatusFilter, filteredDeployments, availableStatuses,
    } = useDeploymentSearchFilter(sortedDeployments)

    const copyUrl = async (url: string) => {
        try {
            await navigator.clipboard.writeText(url)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (e) {
            showError('Copy Failed', e instanceof Error ? e.message : 'Could not access clipboard')
        }
    }

    const handleDeploy = async () => {
        setDeploying(true)
        try {
            const res = await landingPagesApi.deploy(workspaceId, web.Id)
            if (res.error || !res.data) {
                // A 422 refusal is already toasted, readably, by the client's global handler. Toasting it again here
                // showed the same refusal twice, once as raw JSON (ORB-1030).
                if (res.status !== 422) showError('Deploy failed', res.error || 'Unknown error')
                return
            }
            setLandingPage(res.data)
            if (res.data.Deployments) setDeployments(res.data.Deployments)
            else loadDeployments()
            showSuccess('Deployed', 'Landing page is now live.')
            load()
        } finally {
            setDeploying(false)
        }
    }

    const handleRollback = async (versionNumber: number) => {
        setRollbackBusyVersion(versionNumber)
        try {
            const res = await landingPagesApi.rollback(workspaceId, web.Id, versionNumber)
            if (res.error || !res.data) {
                if (res.status !== 422) showError('Rollback failed', res.error || 'Unknown error')
                return
            }
            setLandingPage(res.data)
            if (res.data.Deployments) setDeployments(res.data.Deployments)
            else loadDeployments()
            setConfirmRollbackVersion(null)
            showSuccess('Rolled back', `Landing page is now serving v${versionNumber}.`)
            load()
        } finally {
            setRollbackBusyVersion(null)
        }
    }

    const handleUnpublish = async () => {
        try {
            const res = await landingPagesApi.unpublish(workspaceId, web.Id)
            if (res.error || !res.data) {
                if (res.status !== 422) showError('Unpublish failed', res.error || 'Unknown error')
                return
            }
            setLandingPage(res.data)
            if (res.data.Deployments) setDeployments(res.data.Deployments)
            else loadDeployments()
            setConfirmUnpublish(false)
            showSuccess('Unpublished', 'Landing page is no longer publicly served.')
        } catch {
            showError('Unpublish failed', 'Unknown error')
        }
    }

    // --- Loading state ---
    if (loading) {
        return (
            <div className="flex items-center justify-center py-16 text-slate-500 dark:text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading landing page…
            </div>
        )
    }

    // --- Error state ---
    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <AlertCircle className="w-8 h-8 text-red-500 mb-3" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
        )
    }

    // --- Empty state: no LP exists ---
    if (!landingPage) {
        return (
            <>
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="p-4 rounded-full bg-teal-50 dark:bg-teal-900/20 mb-4">
                        <Globe className="w-8 h-8 text-teal-600 dark:text-teal-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                        No landing page yet
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mb-6">
                        Create and build your landing page in the Assets tab, then come back here to see its status and deployment history.
                    </p>
                    <button
                        type="button"
                        onClick={() => routeToTab('media')}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                    >
                        Go to Assets
                        <ArrowRight className="w-4 h-4" />
                    </button>
                    {canEdit && (
                        <button
                            type="button"
                            onClick={() => setBypassOpen(true)}
                            className="mt-4 text-xs text-slate-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 underline underline-offset-2"
                        >
                            Or use my own URL →
                        </button>
                    )}
                </div>
                <ExternalUrlModal
                    isOpen={bypassOpen}
                    onClose={() => setBypassOpen(false)}
                    onSubmit={async (url) => {
                        const next = await setMode('External', url)
                        if (next) setBypassOpen(false)
                    }}
                    title="Use your own URL"
                    description="Skip the AI builder and point this campaign at a landing page you host elsewhere. Stats, registrations, and email links will still target this URL."
                    submitLabel="Save URL"
                />
            </>
        )
    }

    // --- External-mode card: replaces the entire deploy/version UI ---
    if (isExternalMode) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ExternalLandingPageCard
                        landingPage={landingPage}
                        canEdit={canEdit}
                        onEditUrl={async (url) => { await setMode('External', url) }}
                        onSwitchToBuilder={async () => { await setMode('Builder') }}
                    />

                    <LandingPageAnalyticsCard workspaceId={workspaceId} webId={web.Id} externalMode canExport={canEdit} />
                </div>

                <ExternalLandingPageTracking
                    clientCompanyId={clientCompanyId}
                    workspaceId={workspaceId}
                    campaignId={campaignId}
                    landingPageId={landingPage.Id}
                />
            </div>
        )
    }

    // --- LP exists ---
    const isPublished = !!landingPage.IsPublished
    const deployedUrl = landingPage.DeployedUrl
    // Deployments arrive NEWEST LAST, so this has to search from the end — a plain .find() on a
    // page with history returned the OLDEST Live/Pending row, which then drove the "Deployed: vN"
    // badge and the version-mismatch banner off a long-superseded deploy (ORB-501). Same
    // slice().reverse() idiom as latestFailed just below.
    const liveOrPendingDeployment = landingPage.Deployments?.slice().reverse().find(
        d => d.Status === 'Pending' || d.Status === 'Live'
    )
    const deployedBuilderVersion = liveOrPendingDeployment?.SourceVersionNumber
    const versionMismatch = isPublished && deployedBuilderVersion != null && latestVersionNumber > deployedBuilderVersion
    const latestFailed = landingPage.Deployments?.slice().reverse().find(d => d.Status === 'Failed')
    const isAssetSignedOff = assetStatus === 'Signed Off'
    const showSignOffHint = assetStatus !== null && !isAssetSignedOff
    const hasLiveDeployment = sortedDeployments.some(d => d.Status === 'Live')

    const actionsColumn: ColumnConfig<LandingPageDeploymentDto> = {
        key: 'actions',
        header: '',
        render: d => {
            const canRollback = canEdit
                && hasLiveDeployment
                && d.Status !== 'Live'
                && d.Status !== 'Failed'
                && d.Status !== 'Pending'
            if (!canRollback) return null
            const busy = rollbackBusyVersion === d.VersionNumber
            const confirming = confirmRollbackVersion === d.VersionNumber
            if (confirming) {
                return (
                    <div className="inline-flex items-center gap-1.5">
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleRollback(d.VersionNumber)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                        >
                            {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Rolling…</> : <>Confirm</>}
                        </button>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => setConfirmRollbackVersion(null)}
                            className="px-2.5 py-1 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>
                )
            }
            return (
                <button
                    type="button"
                    onClick={() => setConfirmRollbackVersion(d.VersionNumber)}
                    title={`Roll back to v${d.VersionNumber}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                    <RotateCcw className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                    Rollback
                </button>
            )
        },
    }

    const deploymentColumns: ColumnConfig<LandingPageDeploymentDto>[] = [...DEPLOYMENT_COLUMNS, actionsColumn]

    return (
        <div className="space-y-6">
            {/* Version mismatch banner */}
            {versionMismatch && (
                <div className="flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
                    <AlertTriangle aria-hidden="true" className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                            Newer version available
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                            Version {latestVersionNumber} is the latest, but version {deployedBuilderVersion} is currently published.
                            {canEdit && ' Deploy to publish the latest version.'}
                        </p>
                    </div>
                </div>
            )}

            {/* Two-card top row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Status & Actions Card */}
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-6 space-y-5">
                    {/* Status + version badges */}
                    <div className="flex items-center gap-3 flex-wrap">
                        {isPublished ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400">
                                <Check className="w-4 h-4" />
                                Published
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                Draft
                            </span>
                        )}
                        {deployedBuilderVersion != null && (
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                                Deployed: v{deployedBuilderVersion}
                            </span>
                        )}
                        {latestVersionNumber !== deployedBuilderVersion && (
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                                Latest: v{latestVersionNumber}
                            </span>
                        )}
                    </div>

                    {/* Timestamp */}
                    {landingPage.LastDeployedAt && (
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            Last deployed {shortAgo(landingPage.LastDeployedAt)}
                        </p>
                    )}

                    {/* Last deploy failed warning */}
                    {latestFailed && !isPublished && (
                        <div className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
                            <AlertTriangle className="w-4 h-4" />
                            Last deploy failed{latestFailed.Error ? `: ${latestFailed.Error}` : ''}
                        </div>
                    )}

                    {/* Actions — label / buttons grid */}
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-5">
                        <table className="w-full border-separate" style={{ borderSpacing: '0 8px' }}>
                            <tbody>
                                {/* Deploy row */}
                                {canEdit && (
                                    <tr>
                                        <td className="text-sm font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider pr-4 align-middle w-20">Deploy</td>
                                        <td className="align-middle">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <button
                                                    type="button"
                                                    disabled={deploying || !isAssetSignedOff}
                                                    onClick={handleDeploy}
                                                    className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                                        isPublished
                                                            ? 'border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700'
                                                            : 'bg-primary-600 text-white hover:bg-primary-700'
                                                    }`}
                                                >
                                                    {deploying
                                                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Deploying…</>
                                                        : <><Rocket className={`w-4 h-4 ${isPublished ? 'text-primary-600 dark:text-primary-400' : ''}`} /> {isPublished ? 'Redeploy' : 'Deploy'}</>
                                                    }
                                                </button>
                                                {showSignOffHint && (
                                                    <span className="inline-flex items-center gap-1 text-sm text-amber-700 dark:text-amber-400">
                                                        <AlertTriangle className="w-4 h-4" />
                                                        Sign off the landing page asset to enable {isPublished ? 'redeploy' : 'deploy'}.
                                                    </span>
                                                )}
                                                {isPublished && (
                                                    <>
                                                        <span className="w-px h-5 bg-slate-200 dark:bg-slate-700" />
                                                        {confirmUnpublish ? (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    onClick={handleUnpublish}
                                                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700"
                                                                >
                                                                    <EyeOff className="w-4 h-4" /> Confirm
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setConfirmUnpublish(false)}
                                                                    className="px-3.5 py-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white"
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => setConfirmUnpublish(true)}
                                                                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                                            >
                                                                <EyeOff className="w-4 h-4 text-red-500 dark:text-red-400" /> Unpublish
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {/* Live row */}
                                {isPublished && deployedUrl && (
                                    <tr>
                                        <td className="text-sm font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider pr-4 align-middle w-20">Live</td>
                                        <td className="align-middle">
                                            <div className="flex items-center gap-2">
                                                <a
                                                    href={deployedUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                                >
                                                    <ExternalLink className="w-4 h-4 text-blue-500 dark:text-blue-400" /> View Live
                                                </a>
                                                <span className="w-px h-5 bg-slate-200 dark:bg-slate-700" />
                                                <button
                                                    type="button"
                                                    onClick={() => copyUrl(deployedUrl)}
                                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                                    title="Copy deployed URL"
                                                >
                                                    {copied ? <><Check className="w-4 h-4 text-green-500" /> Copied</> : <><Copy className="w-4 h-4 text-amber-500 dark:text-amber-400" /> Copy URL</>}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {/* Edit row */}
                                <tr>
                                    <td className="text-sm font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider pr-4 align-middle w-20">Edit</td>
                                    <td className="align-middle">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {canEditInWebster && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => routeToTab('media')}
                                                        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                                    >
                                                        <Pencil className="w-4 h-4 text-primary-600 dark:text-primary-400" /> Edit in Webster
                                                    </button>
                                                    <span className="w-px h-5 bg-slate-200 dark:bg-slate-700" />
                                                </>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => routeToTab('media')}
                                                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                            >
                                                <FolderOpen className="w-4 h-4 text-sky-500 dark:text-sky-400" /> View in Assets
                                            </button>
                                            {canEdit && (
                                                <>
                                                    <span className="w-px h-5 bg-slate-200 dark:bg-slate-700" />
                                                    <button
                                                        type="button"
                                                        onClick={() => setBypassOpen(true)}
                                                        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                                    >
                                                        <Globe className="w-4 h-4 text-blue-500 dark:text-blue-400" /> Use my own URL
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>

                                {canEdit && (
                                    <SaveAsTemplateControl
                                        clientCompanyId={clientCompanyId}
                                        workspaceId={workspaceId}
                                        webId={web.Id}
                                        webName={web.Name}
                                    />
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Analytics Card */}
                <LandingPageAnalyticsCard workspaceId={workspaceId} webId={web.Id} canExport={canEdit} />
            </div>

            {/* History is no longer pruned nightly (ORB-501), so this list grows for the life of the
                page and the toolbar's search and status filter are how a user narrows it. Both are
                gated on there being deploys at all: an empty history should not offer a search box
                and a dropdown whose only option is "All statuses". */}
            <DataGrid
                title="Deployments"
                data={filteredDeployments}
                columns={deploymentColumns}
                getRowKey={d => d.VersionNumber}
                pagination
                pageSize={10}
                pageResetKey={`${searchQuery}|${statusFilter}`}
                isLoading={deploymentsLoading}
                emptyMessage={
                    sortedDeployments.length > 0
                        ? 'No deployments match your search or filter.'
                        : canEdit ? 'No deployments yet. Click Deploy above to publish this page.' : 'No deployments yet.'
                }
                defaultSortKey="version"
                defaultSortDirection="desc"
                search={sortedDeployments.length > 0 ? {
                    value: searchQuery,
                    onChange: setSearchQuery,
                    placeholder: 'Search deployments',
                    ariaLabel: 'Search deployments by deploy number, content version, status, or error text',
                } : undefined}
                toolbarActions={sortedDeployments.length > 0 ? (
                    <div className="flex items-center gap-1.5">
                        <Filter aria-hidden="true" className="w-4 h-4 flex-shrink-0 text-slate-400" />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.currentTarget.value)}
                            aria-label="Filter deployments by status"
                            className={`px-2.5 py-1.5 text-xs rounded border ${BRAND_CLASSES.INPUT.NORMAL} focus:outline-none focus:ring-2 ${BRAND_CLASSES.FOCUS.PRIMARY} ${BRAND_CLASSES.FOCUS.BORDER}`}
                        >
                            <option value="all">All statuses</option>
                            {availableStatuses.map((status) => (
                                <option key={status} value={status}>{getDeploymentStatusLabel(status)}</option>
                            ))}
                        </select>
                    </div>
                ) : undefined}
            />

            <ExternalUrlModal
                isOpen={bypassOpen}
                onClose={() => setBypassOpen(false)}
                onSubmit={async (url) => {
                    const next = await setMode('External', url)
                    if (next) setBypassOpen(false)
                }}
                title="Switch to your own URL"
                description="Replace the AI-built page with a URL you host. Builder content stays preserved on the server — you can switch back later."
                submitLabel="Switch to External"
                warning={isPublished ? 'This will replace the deployed Builder URL with your custom URL on all campaign assets and emails.' : undefined}
            />
        </div>
    )
}
